import { DeleteWorkerDialog } from "#src/batteries-included-components/Dialogs/DeleteWorkerDialog";
import { WorkersTableFilterArea } from "#src/batteries-included-components/FilterAreas/WorkersFilterAreas";
import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import { UserDataDisplay } from "#src/components/Common/UserDataDisplay";
import { useListUsers } from "#src/components/hooks/adapters/useUsers";
import { useListWorkers } from "#src/components/hooks/adapters/useWorkers";
import { useTableSortingAndPagination } from "#src/components/Redux/reducers/tableStateReducer";
import { useAuthenticatedContext } from "#src/contexts/AuthenticatedContext.helpers";
import { useRunWorkerDrawer } from "#src/contexts/RunWorkerDrawer";
import { useStickyState } from "#src/hooks/useStickyState";
import { useStorageKey } from "#src/hooks/useStorageKey";
import {
  DataTable,
  DataTablePanel,
  ItemAction,
  TextDataDisplay,
  type HeaderType,
} from "@validereinc/common-components";
import {
  SortDirection,
  UserTypeEnum,
  type UserReturnType,
  type WorkerType,
} from "@validereinc/domain";
import { PermissionsMap } from "@validereinc/domain-controllers";
import React, { useMemo, useState } from "react";
import { WorkerDetailRoutePath } from "./[workerId]/detail";

export const WorkersTablePanel = () => {
  const [filterConfigStorageKey, tableConfigStorageKey, tableStateStorageKey] =
    useStorageKey(
      ["filter-configuration", "table-configuration", "table-state"],
      "workers"
    );
  const [storedFilters] = useStickyState<{ name?: string }>(
    {},
    filterConfigStorageKey
  );
  const filters = useMemo(() => ({ ...storedFilters }), [storedFilters]);

  const [tableState, updateTableState] = useTableSortingAndPagination(
    {
      sortBy: "name",
      sortDirection: SortDirection.ASCENDING,
    },
    filters,
    tableStateStorageKey
  );

  const {
    v2: {
      userInfo: { checkHasPermissions },
    },
  } = useAuthenticatedContext();
  const canRunWorker = checkHasPermissions(PermissionsMap.WorkerRunWrite);
  const canDeleteWorker = checkHasPermissions(PermissionsMap.WorkerDelete);

  const [workerPendingDelete, setWorkerPendingDelete] = useState<
    WorkerType | undefined
  >(undefined);

  const { setSelectedWorkerId, setIsOpen: setRunWorkerDrawerIsOpen } =
    useRunWorkerDrawer();

  const { data: workersData, isLoading: isLoadingWorkers } = useListWorkers({
    page: tableState.page,
    pageSize: tableState.pageSize,
    sortBy: tableState.sortBy,
    sortDirection: tableState.sortDirection,
    filters,
  });
  const workers = workersData?.data ?? [];

  const uniqueUserIds = useMemo(() => {
    const uniqueUserIds = new Set<string>();

    workers?.forEach((worker: WorkerType) => {
      uniqueUserIds.add(worker.machine_user_id);

      if (worker.created_by) {
        uniqueUserIds.add(worker.created_by);
      }
      if (worker.updated_by) {
        uniqueUserIds.add(worker.updated_by);
      }
    });

    return Array.from(uniqueUserIds);
  }, [workers]);

  const { data: usersQueryData, isLoading: isLoadingUsers } = useListUsers(
    {
      filters: {
        id: uniqueUserIds,
        type: [UserTypeEnum.human, UserTypeEnum.machine],
      },
    },
    {
      enabled: Boolean(uniqueUserIds.length),
    }
  );
  const users = usersQueryData?.data ?? [];

  const userIdsToUserDetailsMap = useMemo(() => {
    return Object.fromEntries(users.map((user) => [user.id, user])) as Record<
      string,
      UserReturnType
    >;
  }, [users]);

  const isLoading =
    isLoadingWorkers || (Boolean(uniqueUserIds.length) && isLoadingUsers);

  const headers: Array<HeaderType<WorkerType>> = [
    {
      key: "name",
      label: "Name",
      isSortable: true,
      renderComponent: ({ item }) => (
        <RoutingLink
          to={WorkerDetailRoutePath.toLinkParts({
            pathParams: { workerId: item.id },
          })}
        >
          <TextDataDisplay
            value={item.name}
            isLoading={isLoading}
          />
        </RoutingLink>
      ),
    },
    {
      key: "description",
      label: "Description",
      isSortable: true,
      renderComponent: ({ item }) => (
        <TextDataDisplay
          value={item.description ?? "—"}
          isLoading={isLoading}
          clamp={2}
        />
      ),
    },

    {
      key: "created_at",
      label: "Created At",
      isSortable: true,
      renderComponent: ({ item }) => (
        <DataTable.DataRow.DateCell
          value={item.created_at}
          isLoading={isLoading}
          withTime
        />
      ),
    },
    {
      key: "machine_user_id",
      label: "Machine User",
      isSortable: true,
      renderComponent: ({ item }) => (
        <UserDataDisplay
          id={item.machine_user_id}
          name={userIdsToUserDetailsMap[item.machine_user_id]?.name ?? "—"}
          isLoading={isLoading}
        />
      ),
    },
    {
      key: "updated_by",
      label: "Updated By",
      isSortable: true,
      renderComponent: ({ item }) =>
        item.updated_by ? (
          <UserDataDisplay
            id={item.updated_by}
            name={userIdsToUserDetailsMap[item.updated_by]?.name ?? "—"}
            isLoading={isLoading}
          />
        ) : (
          "-"
        ),
    },
  ];

  const rowActionItems = ({ item }: { item: WorkerType }): ItemAction[] => {
    const actions: ItemAction[] = [];
    if (canRunWorker) {
      actions.push({
        label: "Run Worker",
        buttonProps: {
          onClick: () => {
            setSelectedWorkerId(item.id);
            setRunWorkerDrawerIsOpen(true);
          },
          icon: "play-circle",
        },
      });
    }
    if (canDeleteWorker) {
      actions.push({
        label: "Delete worker",
        buttonProps: {
          onClick: () => setWorkerPendingDelete(item),
          icon: "trash",
        },
      });
    }
    return actions;
  };

  const showRowActions = canRunWorker || canDeleteWorker;

  return (
    <>
      <DeleteWorkerDialog
        worker={workerPendingDelete}
        onClose={() => setWorkerPendingDelete(undefined)}
      />
      <DataTablePanel
        panelProps={{
          title: "Workers",
        }}
        storageKey={tableConfigStorageKey}
        filterComponent={
          <WorkersTableFilterArea
            filterConfigStorageKey={filterConfigStorageKey}
          />
        }
        dataTableProps={{
          intent: "simplicity-first",
          isLoading,
          headers,
          items: workers,
          getItemId: (item: WorkerType) => item.id,
          sorting: {
            sortBy: tableState.sortBy,
            sortDirection: tableState.sortDirection,
          },
          pagination: {
            page: tableState.page,
            pageSize: tableState.pageSize,
            total: workersData?.total_entries ?? 0,
          },
          onSortChange: updateTableState,
          onPaginationChange: updateTableState,
          getItemActions: showRowActions ? rowActionItems : undefined,
        }}
      />
    </>
  );
};
